import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "sonner";
import {
  Save,
  Send,
  Loader2,
  Mail,
  AlertCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Badge } from "../../ui/badge";
import {
  EmailTemplate,
  EmailTemplateKey,
  GET_EMAIL_TEMPLATES,
  PREVIEW_EMAIL_TEMPLATE,
  SEND_TEST_EMAIL,
  UPDATE_EMAIL_TEMPLATE,
} from "../../../graphql/email";
import { TipTapEditor, TipTapEditorHandle } from "./TipTapEditor";
import { VariableInserter } from "./VariableInserter";
import { cn } from "../../../lib/utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Custom debounce hook
function useDebounced<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export const EmailTemplatesTab: React.FC = () => {
  const { data, loading, error, refetch } = useQuery<{
    emailTemplates: EmailTemplate[];
  }>(GET_EMAIL_TEMPLATES, { fetchPolicy: "network-only" });

  const templates = useMemo(() => data?.emailTemplates || [], [data]);
  const [selectedKey, setSelectedKey] = useState<EmailTemplateKey | null>(null);
  const selected =
    templates.find((t) => t.key === selectedKey) || templates[0] || null;

  // Track form state per-template via local state, reset when switching
  const [subject, setSubject] = useState<string>("");
  const [bodyHtml, setBodyHtml] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [focusedField, setFocusedField] = useState<"subject" | "body">("body");
  const [validationError, setValidationError] = useState<string | null>(null);

  // Test email dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState<string>("");

  const subjectInputRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<TipTapEditorHandle | null>(null);

  // Initial template selection
  useEffect(() => {
    if (!selectedKey && templates.length > 0) {
      setSelectedKey(templates[0].key);
    }
  }, [templates, selectedKey]);

  // Sync local form when template changes
  useEffect(() => {
    if (selected) {
      setSubject(selected.subject);
      setBodyHtml(selected.bodyHtml);
      setEnabled(selected.enabled);
      setValidationError(null);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSubject = useDebounced(subject, 500);
  const debouncedBody = useDebounced(bodyHtml, 500);

  const previewInput = useMemo(
    () =>
      selected
        ? {
            key: selected.key,
            subject: debouncedSubject,
            bodyHtml: debouncedBody,
          }
        : null,
    [selected?.key, debouncedSubject, debouncedBody] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { data: previewData, loading: previewLoading } = useQuery(
    PREVIEW_EMAIL_TEMPLATE,
    {
      variables: previewInput ? { input: previewInput } : undefined,
      skip: !previewInput,
      fetchPolicy: "network-only",
    }
  );

  const [updateTemplate, { loading: saving }] = useMutation(
    UPDATE_EMAIL_TEMPLATE,
    {
      refetchQueries: [{ query: GET_EMAIL_TEMPLATES }],
      onCompleted: () => {
        toast.success("Szablon zapisany");
        setValidationError(null);
        refetch();
      },
      onError: (error) => {
        const msg = error.message || "Błąd zapisu szablonu";
        setValidationError(msg);
        toast.error(`Błąd zapisu: ${msg}`);
      },
    }
  );

  const [sendTest, { loading: sending }] = useMutation(SEND_TEST_EMAIL, {
    onCompleted: () => {
      toast.success("Wysłano testowy mail");
      setTestDialogOpen(false);
      setTestEmail("");
    },
    onError: (error) => {
      toast.error(`Błąd wysyłania: ${error.message}`);
    },
  });

  const subjectEmpty = subject.trim() === "";
  const bodyEmpty = bodyHtml.trim() === "" || bodyHtml.trim() === "<p></p>";
  const formInvalid = subjectEmpty || bodyEmpty;

  const hasChanges =
    selected != null &&
    (subject !== selected.subject ||
      bodyHtml !== selected.bodyHtml ||
      enabled !== selected.enabled);

  const handleSave = () => {
    if (!selected || formInvalid) return;
    updateTemplate({
      variables: {
        input: {
          key: selected.key,
          subject: subject.trim(),
          bodyHtml,
          enabled,
        },
      },
    });
  };

  const insertVariable = (varName: string) => {
    const placeholder = `{{${varName}}}`;
    if (focusedField === "subject") {
      const input = subjectInputRef.current;
      if (!input) {
        setSubject((s) => s + placeholder);
        return;
      }
      const start = input.selectionStart ?? subject.length;
      const end = input.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + placeholder + subject.slice(end);
      setSubject(next);
      // Restore caret after React updates
      requestAnimationFrame(() => {
        input.focus();
        const caret = start + placeholder.length;
        input.setSelectionRange(caret, caret);
      });
    } else {
      editorRef.current?.insertText(placeholder);
    }
  };

  const testEmailInvalid =
    testEmail.trim() !== "" && !EMAIL_REGEX.test(testEmail.trim());

  const handleSendTest = () => {
    if (!selected || testEmail.trim() === "" || testEmailInvalid) return;
    sendTest({
      variables: {
        input: {
          to: testEmail.trim(),
          templateKey: selected.key,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-red-600">Nie udało się załadować szablonów</p>
          <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  if (templates.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Brak zdefiniowanych szablonów.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Templates list */}
      <Card className="lg:col-span-1 h-fit">
        <CardHeader>
          <CardTitle className="text-base">Szablony</CardTitle>
          <CardDescription>Wybierz szablon, aby edytować</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {templates.map((tpl) => {
            const isSelected = selected?.id === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => setSelectedKey(tpl.key)}
                className={cn(
                  "w-full text-left rounded-md border px-3 py-2 transition-colors flex items-start justify-between gap-2",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {tpl.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {tpl.subject}
                  </p>
                </div>
                {tpl.enabled ? (
                  <Badge variant="outline" className="text-xs shrink-0">
                    Aktywny
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    Wyłączony
                  </Badge>
                )}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Editor + preview */}
      {selected && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {selected.name}
                </CardTitle>
                <CardDescription>
                  Ostatnia aktualizacja:{" "}
                  {new Date(selected.updatedAt).toLocaleString("pl-PL")}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`tpl-enabled-${selected.id}`}
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label
                  htmlFor={`tpl-enabled-${selected.id}`}
                  className="cursor-pointer"
                >
                  Aktywny
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Subject */}
            <div className="space-y-1">
              <Label htmlFor="subject">Temat wiadomości</Label>
              <Input
                id="subject"
                ref={subjectInputRef}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onFocus={() => setFocusedField("subject")}
                placeholder="Temat..."
              />
              {subjectEmpty && (
                <p className="text-xs text-red-600">Pole wymagane</p>
              )}
            </div>

            {/* Body */}
            <div className="space-y-1">
              <Label>Treść wiadomości</Label>
              <TipTapEditor
                ref={editorRef}
                content={bodyHtml}
                onChange={setBodyHtml}
                onFocus={() => setFocusedField("body")}
                minHeight="260px"
              />
              {bodyEmpty && (
                <p className="text-xs text-red-600">Treść nie może być pusta</p>
              )}
            </div>

            {/* Variables */}
            <VariableInserter
              variables={selected.availableVariables}
              onInsert={insertVariable}
              targetLabel={focusedField === "subject" ? "Temat" : "Treść"}
            />

            {/* Backend validation error */}
            {validationError && (
              <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm">
                <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <div>
                  <p className="font-medium text-red-700 dark:text-red-300">
                    Błąd walidacji
                  </p>
                  <p className="text-xs mt-0.5 text-foreground">
                    {validationError}
                  </p>
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Podgląd</Label>
                {previewLoading && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Aktualizowanie...
                  </span>
                )}
              </div>
              <div className="rounded-md border border-border bg-background overflow-hidden">
                {previewData?.previewEmailTemplate?.subject && (
                  <div className="border-b border-border bg-muted/30 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Temat:</span>{" "}
                    <span className="font-medium text-foreground">
                      {previewData.previewEmailTemplate.subject}
                    </span>
                  </div>
                )}
                <iframe
                  title="Podgląd szablonu"
                  sandbox=""
                  srcDoc={
                    previewData?.previewEmailTemplate?.html ||
                    "<p style='padding:1rem;color:#888;font-family:sans-serif'>Brak podglądu</p>"
                  }
                  className="w-full bg-white"
                  style={{ minHeight: 400, border: 0 }}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving || formInvalid}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? "Zapisywanie..." : "Zapisz szablon"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setTestDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Wyślij testowy z tego szablonu
              </Button>
              {hasChanges && !formInvalid && (
                <span className="text-sm text-amber-600">
                  Niezapisane zmiany
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test send dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wyślij testowy mail</DialogTitle>
            <DialogDescription>
              Szablon: <strong>{selected?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="testEmailDialog">Adres odbiorcy</Label>
            <Input
              id="testEmailDialog"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              autoFocus
            />
            {testEmailInvalid && (
              <p className="text-xs text-red-600">
                Niepoprawny format adresu email
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={sending}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={
                sending || testEmail.trim() === "" || testEmailInvalid
              }
              className="flex items-center gap-2"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Wyślij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
